import React, { useEffect } from 'react';
import { merchantAccessToken } from '../../util/api';
import css from './StripeConnectAccountForm.module.css';

const OnboardingComponent = ({ currentUser }) => {
  const pmsName = 'Kovena';
  const pmsLogo = '';
  const url_api =
    process.env.REACT_APP_KOVENA_EXTRANET_BASE ||
    'https://staging-extranet-api.kovena-v2.whl-staging.com/api/v1';
  const onboardingScriptUrl =
    process.env.REACT_APP_KOVENA_ONBOARDING_SCRIPT_URL ||
    'https://cdn.kovena.com/sandbox/service/assets/js/onboarding/onboarding.min.js';
  const onboardingCssUrl =
    process.env.REACT_APP_KOVENA_ONBOARDING_CSS_URL ||
    'https://cdn.kovena.com/sandbox/service/assets/css/onboarding/onboarding.min.css';
  const referenceId =
    currentUser?.id?.uuid || process.env.REACT_APP_KOVENA_REFERENCE_ID || '0';
  const merchantJoinTimestamp = (() => {
    const createdAt = currentUser?.attributes?.createdAt;
    const createdAtDate = createdAt ? new Date(createdAt) : null;
    return createdAtDate && !isNaN(createdAtDate.valueOf())
      ? Math.floor(createdAtDate.getTime() / 1000)
      : Math.floor(Date.now() / 1000);
  })();
  const profile = currentUser?.attributes?.profile || {};
  const publicData = profile.publicData || {};
  const protectedData = profile.protectedData || {};
  const businessCountry =
    publicData.country ||
    protectedData.country ||
    process.env.REACT_APP_KOVENA_DEFAULT_COUNTRY ||
    'FJ';
  const timeZone =
    publicData.timeZone ||
    protectedData.timeZone ||
    (typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      : '');
  const contactEmail = currentUser?.attributes?.email || '';
  const contactPhone = publicData.phoneNumber || protectedData.phoneNumber || '';
  const displayName = profile.displayName || '';

  /**
   * Load external script safely
   */
  const loadScript = src =>
    new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = () =>
        reject(new Error(`Failed to load script: ${src}`));
      document.body.appendChild(script);
    });

  /**
   * Load external stylesheet safely
   */
  const loadStyle = href =>
    new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = resolve;
      link.onerror = () => reject(new Error(`Failed to load style: ${href}`));
      document.head.appendChild(link);
    });

  /**
   * Initialize Kovena onboarding
   */
  const initOnboarding = async () => {
    try {
      // 1️⃣ Fetch merchant token
      const response = await merchantAccessToken();
      const { data } = response || {};
      const onboardingSettings =
        data?.onboarding_setting || data?.onboarding_settings || {};

      if (!data?.access_token || !data?.url_embed_credit) {
        throw new Error('Invalid response from merchantAccessToken');
      }

      // 2️⃣ Required globals
      window.EmbedOnboardingAccessToken = data.access_token;
      window.partner_id = data.partner_id;

      // 3️⃣ Load Kovena stylesheet
      await loadStyle(onboardingCssUrl);

      // 4️⃣ Load Kovena SDK
      await loadScript(onboardingScriptUrl);

      // 5️⃣ Load embed credit script
      await loadScript(data.url_embed_credit);

      // 6️⃣ Resolve onboarding object
      const onboarding =
        window.OnboardingApp?.start
          ? window.OnboardingApp
          : window.OnboardingApp?.default?.start
          ? window.OnboardingApp.default
          : window.KovenaOnboarding?.start
          ? window.KovenaOnboarding
          : null;

      if (!onboarding || typeof onboarding.start !== 'function') {
        console.error('Kovena onboarding start() not available', {
          OnboardingApp: window.OnboardingApp,
          KovenaOnboarding: window.KovenaOnboarding,
        });
        return;
      }

      const container = document.getElementById('OnboardingApp');
      if (!container) {
        console.error('OnboardingApp container not found');
        return;
      }

      // 6️⃣ Start onboarding (Country is REQUIRED)
      const onboardingInstance = onboarding.start({
        OnboardingElement: container,
        EndpointApi: url_api,
        PmsName: pmsName,
        PmsLogo: pmsLogo,
        ReferenceId: referenceId,
        PublicKey: process.env.KOVENA_PUBLIC_KEY,
        partnerId: window.partner_id,
        MerchantPmsDateJoin: merchantJoinTimestamp,
        OnboardingSettings: onboardingSettings,

        // ✅ REQUIRED BY KOVENA
        DefaultValueBusinessDetails: {
          PropertyAdministrativeContact: displayName,
          PropertyName: publicData.propertyName || '',
          PropertyContactEmail: contactEmail,
          PropertyPhoneNumber: contactPhone,
          AuthorizedSignatory: publicData.authorizedSignatory || displayName,
          AuthorizedSignatoryTitle: publicData.authorizedSignatoryTitle || '',
          Country: businessCountry, // ISO-2 code
          Timezone: timeZone,
        },
      });

      // 7️⃣ Attach basic event listeners for debugging
      const addListener = (eventName, handler) => {
        if (!container) return;
        if (typeof container.addEventListener === 'function') {
          container.addEventListener(eventName, handler);
        } else if (window.$ && typeof window.$ === 'function') {
          window.$(container).on(eventName, handler);
        }
      };

      addListener('onMerchantCreationSuccess', (event, merchantInfo) => {
        console.log('Kovena merchant created:', merchantInfo || event);
      });
      addListener('onMerchantCreationFailed', (event, error) => {
        console.error('Kovena merchant creation failed:', error || event);
      });
      addListener('onKycCreationSuccess', (event, kycInfo) => {
        console.log('Kovena KYC created:', kycInfo || event);
      });
      addListener('onKycCreationFailed', (event, error) => {
        console.error('Kovena KYC failed:', error || event);
      });
      addListener('onValidationFailed', (event, error) => {
        console.error('Kovena validation failed:', error || event);
      });

      if (!onboardingInstance) {
        console.warn('Kovena onboarding did not return an instance');
      }
    } catch (error) {
      console.error('Kovena onboarding initialization failed:', error);
    }
  };

  /**
   * Auto-start on mount
   */
  useEffect(() => {
    initOnboarding();
  }, []);

  return (
    <div className={css.onboardingWrapper}>
      {/* Kovena renders UI here */}
      <div id="OnboardingApp" />
    </div>
  );
};

export default OnboardingComponent;
